/**
 * orderMirror.js — whole-business-day order blueprint
 *
 * Every order this device learns about — fetched from the server, delivered
 * over the socket, or created/edited locally — is upserted into the
 * order_mirror store (SQLite in Electron, IndexedDB on web). The mirror is
 * what keeps table occupancy, the dine-in view, and the History tab working
 * through internet outages of any length (some branches lose internet for a
 * whole day).
 *
 * HARD RULES (the two danger points):
 *  1. The mirror is READ-ONLY for sync. Uploads come exclusively from
 *     offline_orders (the unsynced queue). Nothing here can ever cause a
 *     duplicate row in Supabase.
 *  2. Day cleanup deletes ONLY synced rows. An unsynced order survives any
 *     number of day rollovers until the server has confirmed it.
 *
 * Identity rule (duplicate prevention in the UI):
 *  - order_key = server id when the order has one, else its local_id.
 *  - A row first keyed by local_id is REPLACED when the same order arrives
 *    with a server id (storage layer deletes the local-keyed row in the same
 *    transaction). One order ↔ one row, always.
 *  - NOTE: `order.serverId` in this codebase is the WAITER's user id, never
 *    order identity. Identity comes from `_id` / `id` only.
 */

import localDB from './localDB';

const ACTIVE_STATUSES = ['confirmed', 'preparing', 'ready', 'served', 'billed'];
// Completed/cancelled orders older than this never enter the mirror — it
// keeps History-tab fetches of PAST days (which also land in Redux) from
// being re-stamped with the current business date.
const COMPLETED_MAX_AGE_MS = 48 * 60 * 60 * 1000;

const isLocalish = (id) => typeof id === 'string' && id.startsWith('local_');
const isDraft = (order) => {
  const oid = String(order?._id || order?.id || '');
  return oid.startsWith('draft_') || order?.status === 'draft' || order?.isDraft === true;
};

/** "YYYY-MM-DD" today in Europe/Istanbul (project rule: static TR timezone). */
function istanbulToday() {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Istanbul',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
}

/**
 * Business date for mirroring = the OPEN session's date (session mode: an
 * open session owns every order it takes, even past midnight), falling back
 * to the Istanbul calendar date.
 */
async function resolveBusinessDate(branchId) {
  try {
    const session = await localDB.getActiveSession(branchId);
    const sd = session?.session_date || session?.sessionDate;
    if (typeof sd === 'string' && sd.length >= 10) return sd.slice(0, 10);
  } catch (_) { /* fall through */ }
  return istanbulToday();
}

function orderIdentity(order) {
  const primary = String(order?._id || order?.id || '');
  const explicitLocal = order?.localId || order?.local_id || null;
  if (!primary && !explicitLocal) return null;
  const serverId = primary && !isLocalish(primary) ? primary : null;
  const localId = explicitLocal || (isLocalish(primary) ? primary : null);
  return {
    orderKey: serverId || localId,
    serverId,
    localId,
    synced: !!serverId,
  };
}

function toRow(order, branchId, businessDate) {
  const idn = orderIdentity(order);
  if (!idn || !idn.orderKey) return null;
  const status = String(order?.status || '').toLowerCase();
  const isActive = ACTIVE_STATUSES.includes(status);
  if (!isActive) {
    // Non-active order: only mirror recent ones (today's completions for the
    // offline History tab) — never old history pages.
    const createdMs = new Date(order?.createdAt || order?.created_at || 0).getTime();
    if (!Number.isFinite(createdMs) || Date.now() - createdMs > COMPLETED_MAX_AGE_MS) return null;
  }
  const now = new Date().toISOString();
  return {
    order_key: idn.orderKey,
    server_id: idn.serverId,
    local_id: idn.localId,
    branch_id: branchId,
    status,
    order_type: order?.orderType || order?.order_type || '',
    business_date: businessDate,
    session_id: order?.session_id || order?.sessionId || null,
    synced: idn.synced ? 1 : 0,
    data: JSON.stringify(order),
    updated_at: order?.updatedAt || order?.updated_at || now,
    created_at: order?.createdAt || order?.created_at || now,
  };
}

let _lastWriteFailureLogAt = 0;

const orderMirror = {
  /**
   * Mirror a batch of orders. Never throws; failures are logged and ignored —
   * the mirror must never break an order flow.
   */
  async mirrorOrders(orders, fallbackBranchId = null) {
    try {
      const list = (orders || []).filter((o) => o && !isDraft(o));
      if (list.length === 0) return;
      const branchId =
        list[0]?.branchId || list[0]?.branch_id || fallbackBranchId ||
        (typeof localStorage !== 'undefined' ? localStorage.getItem('lastSelectedBranch') : null);
      if (!branchId) {
        console.warn('[mirror] skipped batch — no branch id resolvable', { count: list.length });
        return;
      }
      const businessDate = await resolveBusinessDate(branchId);
      const rows = list.map((o) => toRow(o, branchId, businessDate)).filter(Boolean);
      if (rows.length === 0) return;
      const res = await localDB.mirrorUpsertMany(rows);
      // A mirror that silently stops persisting is how "tables vanished
      // offline" comes back — surface non-success in app_logs, throttled to
      // one line per 5 minutes so a broken machine doesn't flood the feed.
      if (!res || res.success !== true) {
        const now = Date.now();
        if (now - _lastWriteFailureLogAt > 5 * 60 * 1000) {
          _lastWriteFailureLogAt = now;
          console.error('[mirror] writes are NOT persisting on this machine', {
            branchId, attempted: rows.length, result: res || null,
          });
        }
      }
    } catch (err) {
      console.warn('[mirror] mirrorOrders failed (non-fatal)', { errMsg: String(err?.message || err) });
    }
  },

  /**
   * Explicit local→server identity migration after sync (localIdMap from the
   * sync response). Guarantees the local-keyed row is replaced even when the
   * remapped Redux entity no longer carries its old localId.
   */
  async remapIds(localIdMap, entities, fallbackBranchId = null) {
    try {
      const pairs = Object.entries(localIdMap || {});
      if (pairs.length === 0) return;
      const rowsInput = [];
      for (const [localId, mapped] of pairs) {
        const serverId = mapped && typeof mapped === 'object' ? (mapped.id || mapped._id) : mapped;
        if (!serverId) continue;
        const order = entities?.[serverId] || (mapped && typeof mapped === 'object' ? mapped : null);
        if (!order) continue;
        rowsInput.push({ ...order, _id: serverId, localId });
      }
      if (rowsInput.length === 0) return;
      await this.mirrorOrders(rowsInput, fallbackBranchId);
      console.warn('[mirror] remapped local→server ids', { count: rowsInput.length });
    } catch (err) {
      console.warn('[mirror] remapIds failed (non-fatal)', { errMsg: String(err?.message || err) });
    }
  },

  /** Active orders from the mirror (offline occupancy/dine-in). */
  async getActive(branchId) {
    return localDB.mirrorGetActive(branchId);
  },

  /**
   * Reconcile the mirror against a fresh AUTHORITATIVE server active list
   * (the fulfilled result of a full getActiveOrders fetch — never a partial
   * or per-table response). Any mirror row that has a SERVER id but is absent
   * from the server list was completed/cancelled on another device — mark it
   * completed here so local hydration can never resurrect it as a ghost
   * occupied table. Unsynced (local_-only) rows are untouchable: absence from
   * the server just means they haven't uploaded yet.
   */
  async tombstoneAbsent(serverOrders, branchId) {
    try {
      if (!branchId || !Array.isArray(serverOrders)) return;
      const serverIds = new Set(
        serverOrders.map((o) => String(o?._id || o?.id || '')).filter((id) => id && !isLocalish(id))
      );
      const mirrorActive = await this.getActive(branchId);
      if (!Array.isArray(mirrorActive) || mirrorActive.length === 0) return;
      const businessDate = await resolveBusinessDate(branchId);
      const now = new Date().toISOString();
      const rows = [];
      for (const order of mirrorActive) {
        const idn = orderIdentity(order);
        if (!idn?.serverId) continue;            // unsynced — not ours to judge
        if (serverIds.has(idn.serverId)) continue; // still active on the server
        const dead = { ...order, status: 'completed' };
        rows.push({
          order_key: idn.orderKey,
          server_id: idn.serverId,
          local_id: idn.localId,
          branch_id: branchId,
          status: 'completed',
          order_type: order?.orderType || order?.order_type || '',
          business_date: businessDate,
          session_id: order?.session_id || order?.sessionId || null,
          synced: 1,
          data: JSON.stringify(dead),
          updated_at: now,
          created_at: order?.createdAt || order?.created_at || now,
        });
      }
      // ── Orphaned UNSYNCED rows ────────────────────────────────────────────
      // An unsynced mirror row is precious only while a matching row still
      // waits in the upload queue. Once the queue holds nothing for it, it can
      // never reach the server — yet three sweeps all decline to touch it
      // (tombstoning skips rows without a server id, day cleanup spares
      // unsynced rows by design, and re-hydration keeps refreshing its
      // business date). The result is an immortal ghost that occupies its
      // table on one machine forever: Ayder carried a ₺2,640 card for four
      // days (2026-08-13), left over from a pre-1.0.55 sync that wiped the
      // whole queue at once without remapping the mirror.
      // Marked CANCELLED, not completed: it never became a real order, and
      // cancelled rows are also eligible for the day sweep.
      // ── Orphaned UNSYNCED rows — DIAGNOSTIC ONLY (do not delete) ──────────
      // An earlier version of this swept unsynced mirror rows that had no
      // matching queue row, to clear immortal ghost tables. It was WRONG and
      // destroyed live work: at Kalkinma on 2026-08-14 it cancelled five
      // real, current orders (L-0076…L-0080, none of them yet on the server)
      // seconds after app start, and their tables vanished off the floor.
      //
      // The flaw: "no queue row" does NOT prove orphanhood. The queue read
      // returns an empty list transiently while SQLite IPC is still coming up
      // at boot (the same race Phase A had to add retries for) — the very
      // moment this sweep runs. An empty queue is indistinguishable from
      // "storage not ready yet", so it can never justify deleting anything.
      //
      // Until there is positive proof of orphanhood, only REPORT candidates.
      // The Ayder-style ghost stays visible; a visible ghost is far cheaper
      // than a deleted order.
      try {
        const queueRows = await localDB.getAllOrders(branchId);
        const queueKeys = new Set(
          (Array.isArray(queueRows) ? queueRows : [])
            .flatMap((o) => [o?.localId, o?._id, o?.id])
            .filter(Boolean)
            .map(String)
        );
        const candidates = [];
        for (const order of mirrorActive) {
          const idn = orderIdentity(order);
          if (!idn || idn.serverId) continue;
          if (queueKeys.has(String(idn.localId)) || queueKeys.has(String(idn.orderKey))) continue;
          candidates.push({
            orderKey: idn.orderKey,
            orderNumber: order?.orderNumber || order?.order_number || null,
            total: order?.total ?? null,
            createdAt: order?.createdAt || order?.created_at || null,
          });
        }
        if (candidates.length > 0) {
          console.warn('[mirror] unsynced rows with no queue row (NOT touched — diagnostic)', {
            branchId,
            queueRowsRead: Array.isArray(queueRows) ? queueRows.length : null,
            candidates: candidates.slice(0, 10),
          });
        }
      } catch (_) { /* diagnostic only */ }

      if (rows.length === 0) return;
      await localDB.mirrorUpsertMany(rows);
      console.warn('[mirror] tombstoned rows absent from authoritative server list', {
        branchId, tombstoned: rows.length, serverActive: serverIds.size,
      });
    } catch (err) {
      console.warn('[mirror] tombstoneAbsent failed (non-fatal)', { errMsg: String(err?.message || err) });
    }
  },

  /** Every mirrored order of the current business day (offline History tab). */
  async getCurrentDay(branchId) {
    const businessDate = await resolveBusinessDate(branchId);
    const orders = await localDB.mirrorGetDay(branchId, businessDate);
    return { businessDate, orders };
  },

  /**
   * Day-boundary cleanup — call when a session starts. Deletes SYNCED rows of
   * previous business days only; unsynced rows are kept forever (until they
   * reach the server). Idempotent and cheap, safe to call repeatedly.
   */
  async cleanupOldDays(branchId) {
    try {
      const keepDate = await resolveBusinessDate(branchId);
      const res = await localDB.mirrorCleanupDays(branchId, keepDate);
      // Always warn — this is a key fleet-health signal in app_logs:
      // `deleted` shows storage hygiene, `unsyncedLeft` > 0 means a previous
      // day still has orders that NEVER reached the server (needs attention).
      console.warn('[mirror] day cleanup', {
        branchId,
        keepDate,
        deleted: res?.deleted ?? 0,
        unsyncedLeft: res?.unsyncedLeft ?? 0,
      });
      return res;
    } catch (err) {
      console.warn('[mirror] cleanup failed (non-fatal)', { errMsg: String(err?.message || err) });
      return { success: false };
    }
  },
};

export default orderMirror;
